'use client';
import React, { useState } from 'react';
import { CheckCircle, Clock, AlertTriangle, X, Shield } from 'lucide-react';

export type VerificationStatus = 'PENDING' | 'WEB_CHECKED' | 'BUYER_VERIFIED' | 'FLAGGED';

interface VerificationBadgeProps {
  status: VerificationStatus;
  webVerifySummary?: string;
  size?: 'sm' | 'md';
}

const statusConfig: Record<VerificationStatus, {
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  colorClass: string;
  bgClass: string;
  borderClass: string;
  clickable: boolean;
}> = {
  PENDING:        { label: 'Pending',        icon: Clock,        colorClass: 'text-muted-foreground', bgClass: 'bg-muted/50',      borderClass: 'border-border',           clickable: false },
  WEB_CHECKED:    { label: 'Web Checked',    icon: CheckCircle,  colorClass: 'text-secondary',        bgClass: 'bg-secondary/10',  borderClass: 'border-secondary/30',     clickable: true  },
  BUYER_VERIFIED: { label: 'Buyer Verified', icon: Shield,       colorClass: 'text-primary',          bgClass: 'bg-primary/10',    borderClass: 'border-primary/30',       clickable: false },
  FLAGGED:        { label: 'Flagged',        icon: AlertTriangle, colorClass: 'text-red-400',         bgClass: 'bg-red-500/10',    borderClass: 'border-red-500/30',       clickable: false },
};

export default function VerificationBadge({ status, webVerifySummary, size = 'md' }: VerificationBadgeProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const config = statusConfig[status];
  const IconComponent = config.icon;
  const isClickable = config.clickable && webVerifySummary;

  return (
    <>
      <button
        type="button"
        onClick={() => isClickable && setModalOpen(true)}
        className={`
          inline-flex items-center gap-1.5 rounded-full border font-mono-data font-medium
          ${size === 'sm' ? 'text-[10px] px-2 py-0.5' : 'text-xs px-2.5 py-1'}
          ${config.colorClass} ${config.bgClass} ${config.borderClass}
          ${isClickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : 'cursor-default'}
        `}
        disabled={!isClickable}
        aria-label={`Verification status: ${config.label}${isClickable ? '. Click to see details.' : ''}`}
      >
        <IconComponent size={size === 'sm' ? 10 : 12} className={config.colorClass} />
        {config.label}
      </button>

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in"
          onClick={() => setModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Web verification details"
        >
          <div
            className="surface-elevated max-w-md w-full p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg text-foreground mb-1">Independent Verification Guidance</h3>
                <p className="text-xs text-muted-foreground">AI-generated guidance for buyers</p>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
                aria-label="Close modal"
              >
                <X size={16} />
              </button>
            </div>
            <div className="bg-secondary/5 border border-secondary/20 rounded-lg p-4">
              <p className="text-sm text-foreground leading-relaxed">{webVerifySummary}</p>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              This guidance is AI-generated based on anonymised profile attributes only. No personal identifiers were used.
            </p>
          </div>
        </div>
      )}
    </>
  );
}